import { lazy } from "react";

// project import
import MainLayout from "layout/MainLayout";
import CommonLayout from "layout/CommonLayout";
import Loadable from "components/Loadable";
import AuthGuard from "utils/route-guard/AuthGuard";
import PermissionGuard from "utils/route-guard/PermissionGuard";
import RentGuard from "utils/route-guard/RentGuard";

// pages routing
const MaintenanceError = Loadable(lazy(() => import("pages/maintenance/404")));
const MaintenanceError500 = Loadable(
  lazy(() => import("pages/maintenance/500"))
);
const MaintenanceUnderConstruction = Loadable(
  lazy(() => import("pages/maintenance/under-construction"))
);
const MaintenanceComingSoon = Loadable(
  lazy(() => import("pages/maintenance/coming-soon"))
);

// render - sample page
const SamplePage = Loadable(
  lazy(() => import("pages/extra-pages/sample-page"))
);

// render - dashboard
const Dashboard = Loadable(lazy(() => import("pages/dashboard/dashboard")));

// render - applications
// ------------------------------- USER ----------------------------------
const AppUserList = Loadable(lazy(() => import("pages/apps/user/list")));
const AppUserEdit = Loadable(lazy(() => import("pages/apps/user/edit")));
const UserTabPersonal = Loadable(
  lazy(() => import("sections/apps/user/TabPersonal"))
);
const UserPermissionsTab = Loadable(
  lazy(() => import("sections/apps/user/TabPermissions"))
);
const UserCompanyTab = Loadable(
  lazy(() => import("sections/apps/user/TabCompany"))
);

// ------------------------------- STORE ----------------------------------
const AppStoreList = Loadable(lazy(() => import("pages/apps/store/list")));
const AppStoreCategoryList = Loadable(
  lazy(() => import("pages/apps/store/categories-list"))
);

// ------------------------------- LAND ----------------------------------
const AppCompanyList = Loadable(lazy(() => import("pages/apps/company/list")));

// ------------------------------- LAND ----------------------------------
const AppLandList = Loadable(lazy(() => import("pages/apps/land/list")));
const AppLandDetail = Loadable(lazy(() => import("pages/apps/land/detail")));

// ------------------------------- LAND ----------------------------------
const RentingRequestsList = Loadable(
  lazy(() => import("pages/apps/renting_requests/list"))
);

// ------------------------------- ACTIVITY LOG ----------------------------------
const AppActivityLogList = Loadable(
  lazy(() => import("pages/apps/activity_log/list"))
);

// ------------------------------- VENDING MACHINE ----------------------------------
const AppVendingMachineList = Loadable(
  lazy(() => import("pages/apps/vending_machine/list"))
);
const AppVendingMachineCategoryList = Loadable(
  lazy(() => import("pages/apps/vending_machine/categories-list"))
);

// ------------------------------- PRODUCT ----------------------------------
const AppProductList = Loadable(lazy(() => import("pages/apps/product/list")));
const AppProductCategoryList = Loadable(
  lazy(() => import("pages/apps/product/categories-list"))
);

// ------------------------------- LOGISTICS ----------------------------------
const AppLogisticPackageList = Loadable(
  lazy(() => import("pages/apps/logistics/list"))
);

// ------------------------------- STOCK ----------------------------------
const AppStockList = Loadable(lazy(() => import("pages/apps/stock/list")));

// ------------------------------- CONTRACT ----------------------------------
const ContractStockList = Loadable(
  lazy(() => import("pages/apps/contract/list"))
);

// ------------------------------- TRANSACTION ----------------------------------
const AppTransactionList = Loadable(
  lazy(() => import("pages/apps/transactions/list"))
);

// ------------------------------- NOTIFICATION ----------------------------------
const AppNotificationList = Loadable(
  lazy(() => import("pages/apps/notifications/list"))
);

// ------------------------------- PAYMENT ----------------------------------
const PaymentList = Loadable(lazy(() => import("pages/apps/payments/list")));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: "/",
  children: [
    {
      path: "/",
      element: (
        <RentGuard>
          <AuthGuard>
            <MainLayout />
          </AuthGuard>
        </RentGuard>
      ),
      children: [
        {
          path: "dashboard",
          element: (
            <PermissionGuard permission="view_dashboard">
              <Dashboard />
            </PermissionGuard>
          ),
        },
        {
          path: "administration",
          element: (
            <PermissionGuard permission="view_administration">
              <SamplePage />
            </PermissionGuard>
          ),
        },
        {
          path: "activity_log",
          element: (
            <PermissionGuard permission="view_activitylog">
              <AppActivityLogList />
            </PermissionGuard>
          ),
        },
        {
          path: "users",
          children: [
            {
              path: "",
              element: (
                <PermissionGuard permission="view_user">
                  <AppUserList />
                </PermissionGuard>
              ),
            },
            {
              path: ":id/edit",
              element: (
                <PermissionGuard permission="view_user">
                  <AppUserEdit />
                </PermissionGuard>
              ),
              children: [
                {
                  path: "personal",
                  element: (
                    <PermissionGuard permission="change_user">
                      <UserTabPersonal />
                    </PermissionGuard>
                  ),
                },
                {
                  path: "company",
                  element: (
                    <PermissionGuard permission="change_user">
                      <UserCompanyTab />
                    </PermissionGuard>
                  ),
                },
                {
                  path: "permissions",
                  element: (
                    <PermissionGuard permission="change_user">
                      <UserPermissionsTab />
                    </PermissionGuard>
                  ),
                },
              ],
            },
          ],
        },
        {
          path: "reports",
          children: [
            {
              path: "",
              element: (
                <PermissionGuard permission="view_report">
                  <SamplePage />
                </PermissionGuard>
              ),
            },
          ],
        },
        {
          path: "companies",
          children: [
            {
              path: "",
              element: (
                <PermissionGuard permission="view_company">
                  <AppCompanyList />
                </PermissionGuard>
              ),
            },
          ],
        },
        {
          path: "lands",
          children: [
            {
              path: "",
              element: (
                <PermissionGuard permission="view_land">
                  <AppLandList />
                </PermissionGuard>
              ),
            },
            {
              path: ":id",
              element: (
                <PermissionGuard permission="view_land">
                  <AppLandDetail />
                </PermissionGuard>
              ),
            },
          ],
        },

        {
          path: "renting",
          children: [
            {
              path: "",
              element: (
                <PermissionGuard permission="view_sendingrequest">
                  <RentingRequestsList />
                </PermissionGuard>
              ),
            },
          ],
        },
        {
          path: "stores",
          children: [
            {
              path: "",
              element: (
                <PermissionGuard permission="view_store">
                  <AppStoreList />
                </PermissionGuard>
              ),
            },
            {
              path: "categories",
              element: (
                <PermissionGuard permission="view_storecategory">
                  <AppStoreCategoryList />
                </PermissionGuard>
              ),
            },
          ],
        },
        {
          path: "vending-machines",
          children: [
            {
              path: "",
              element: (
                <PermissionGuard permission="view_vendingmachine">
                  <AppVendingMachineList />
                </PermissionGuard>
              ),
            },
            {
              path: "categories",
              element: (
                <PermissionGuard permission="view_vendingmachinecategory">
                  <AppVendingMachineCategoryList />
                </PermissionGuard>
              ),
            },
          ],
        },
        {
          path: "products",
          children: [
            {
              path: "",
              element: (
                <PermissionGuard permission="view_product">
                  <AppProductList />
                </PermissionGuard>
              ),
            },
            {
              path: "categories",
              element: (
                <PermissionGuard permission="view_productcategory">
                  <AppProductCategoryList />
                </PermissionGuard>
              ),
            },
          ],
        },
        {
          path: "logistics",
          children: [
            {
              path: "",
              element: (
                <PermissionGuard permission="view_logistics">
                  <AppLogisticPackageList />
                </PermissionGuard>
              ),
            },
          ],
        },
        {
          path: "stock",
          children: [
            {
              path: "",
              element: (
                <PermissionGuard permission="view_stock">
                  <AppStockList />
                </PermissionGuard>
              ),
            },
          ],
        },
        {
          path: "notifications",
          children: [
            {
              path: "",
              element: (
                // <PermissionGuard permission="view_stock">
                <AppNotificationList />
                // </PermissionGuard>
              ),
            },
          ],
        },
        {
          path: "transactions",
          children: [
            {
              path: "",
              element: (
                <PermissionGuard permission="view_transaction">
                  <AppTransactionList />
                </PermissionGuard>
              ),
            },
          ],
        },
        {
          path: "maintenance",
          children: [
            {
              path: "",
              element: (
                <PermissionGuard permission="view_maintenanceschedule">
                  <SamplePage />
                </PermissionGuard>
              ),
            },
          ],
        },
        {
          path: "payments",
          children: [
            {
              path: "",
              element: (
                <PermissionGuard permission="view_payment">
                  <PaymentList />
                </PermissionGuard>
              ),
            },
            {
              path: "methods",
              element: (
                <PermissionGuard permission="view_payment_method">
                  <SamplePage />
                </PermissionGuard>
              ),
            },
          ],
        },
        // {
        //   path: "settings",
        //   children: [
        //     {
        //       path: "",
        //       element: (
        //         <PermissionGuard permission="view_setting">
        //           <SamplePage />
        //         </PermissionGuard>
        //       ),
        //     },
        //   ],
        // },
        {
          path: "contracts",
          children: [
            {
              path: "",
              element: (
                <PermissionGuard permission="view_contract">
                  <ContractStockList />
                </PermissionGuard>
              ),
            },
          ],
        },
      ],
    },
    {
      path: "/maintenance",
      element: <CommonLayout />,
      children: [
        {
          path: "404",
          element: <MaintenanceError />,
        },
        {
          path: "500",
          element: <MaintenanceError500 />,
        },
        {
          path: "under-construction",
          element: <MaintenanceUnderConstruction />,
        },
        {
          path: "coming-soon",
          element: <MaintenanceComingSoon />,
        },
      ],
    },
  ],
};

export default MainRoutes;
